import { GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import constate from 'constate';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useAgGridSelection } from 'core/hooks';
import { AgGridDataRow, AgGridTransactionOptions } from 'core/types';
import { ICustomGridOptionsParams } from 'core/utils';

import { AgGridCellUpdateProvider } from './agGridCellUpdate';

/**
 * Shared hook that provides common AgGrid functionality
 * Used by both agGridServerTable and agGridTable hooks
 */

interface IProps
  extends Omit<
    ICustomGridOptionsParams,
    | 'onCellValueChanged'
    | 'onCellEditingStarted'
    | 'onCellEditingStopped'
    | 'getRowId'
    | 'isRowSelectable'
  > {}

const useAgGridTable = ({ onSelectionChanged: customOnSelectionChanged }: IProps) => {
  const [gridApi, setGridApi] = useState<GridApi<AgGridDataRow> | null>(null);

  const gridRef = useRef<AgGridReact>(null);
  const gridRefCurrent = gridRef.current;

  useEffect(() => {
    setGridApi(gridRefCurrent?.api);
  }, [gridRefCurrent]);

  const { selectedRows, selectedRowsIds, onSelectionChanged, deselectAllRows } = useAgGridSelection(
    {
      gridRef,
      customOnSelectionChanged,
    }
  );

  const getCurrentRows = useCallback(() => {
    if (!gridApi || gridApi?.isDestroyed()) {
      return [];
    }

    const currentRows = [];

    gridApi.forEachNode((node) => {
      if (node.data) {
        currentRows.push(node.data);
      }
    });

    return currentRows;
  }, [gridApi]);

  const onRowsUpdated = useCallback(
    ({
      refreshCells = false,
      redrawRows = false,
      goToRowIndex = null,
      skipDeselect = false,
    }: AgGridTransactionOptions = {}) => {
      if (!gridApi) {
        return;
      }

      if (refreshCells) {
        gridApi.refreshCells({ force: true });
      }

      if (redrawRows) {
        gridApi.redrawRows();
      }

      if (goToRowIndex) {
        gridApi.ensureIndexVisible(goToRowIndex, 'bottom');
      }

      if (!skipDeselect) {
        deselectAllRows();
      }
    },
    [gridApi, deselectAllRows]
  );

  return {
    gridRef,
    gridApi,
    getCurrentRows,
    selectedRows,
    selectedRowsIds,
    onSelectionChanged,
    deselectAllRows,
    onRowsUpdated,
  };
};

export const [AgGridTableProviderWithoutCellUpdate, useAgGridTableContext] =
  constate(useAgGridTable);

export const AgGridTableProvider = ({ children }: { children: ReactNode }) => {
  return (
    <AgGridCellUpdateProvider>
      <AgGridTableProviderWithoutCellUpdate>{children}</AgGridTableProviderWithoutCellUpdate>
    </AgGridCellUpdateProvider>
  );
};
